import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export const SETTING_KEYS = {
  BEWERBER_EMAIL_FLOW_ENABLED: 'bewerberEmailFlowEnabled',
  /** User-IDs, die bei Änderungen an Schule/Stundenplan eines Bedarfs eine E-Mail bekommen. */
  BEDARF_UPDATE_RECIPIENTS: 'bedarfUpdateRecipients',
} as const;

export type SettingKey = (typeof SETTING_KEYS)[keyof typeof SETTING_KEYS];

export interface SystemSetting<T = unknown> {
  key: string;
  value: T;
  description: string | null;
  updatedAt: string;
  createdAt: string;
  updatedBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export const settingsKeys = {
  all: ['settings'] as const,
  list: () => [...settingsKeys.all, 'list'] as const,
  detail: (key: string) => [...settingsKeys.all, 'detail', key] as const,
};

const fetchSettings = async (): Promise<ApiResponse<SystemSetting[]>> => {
  const response = await apiClient.get('/settings');
  return response.data;
};

const updateSetting = async ({
  key,
  value,
}: {
  key: string;
  value: unknown;
}): Promise<ApiResponse<SystemSetting>> => {
  const response = await apiClient.patch(`/settings/${key}`, { value });
  return response.data;
};

export function useSettings() {
  return useQuery({
    queryKey: settingsKeys.list(),
    queryFn: fetchSettings,
    select: (response) => response.data,
  });
}

export function useUpdateSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSetting,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
    },
  });
}
